import React, { useState, useEffect, useCallback } from 'react'
import { 
  Users, 
  Search, 
  Filter, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  Eye,
  Trash2,
  Play,
  Pause,
  Calendar,
  Mail,
  MapPin,
  Hash,
  AlertTriangle,
  Square,
  Key,
  RotateCcw,
  Download,
  Award,
  BarChart3
} from 'lucide-react'
import { sessionAPI } from '../services/api.jsx'
import certificateAPI from '../services/certificateAPI'
import { formatDistanceToNow, format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'

const Sessions = () => {
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [huntFilter, setHuntFilter] = useState('all')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [selectedSession, setSelectedSession] = useState(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [sessionToDelete, setSessionToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [sessionToRefund, setSessionToRefund] = useState(null)
  const [refunding, setRefunding] = useState(false)
  const [refundReason, setRefundReason] = useState('')
  const [progressData, setProgressData] = useState(null)
  const [loadingProgress, setLoadingProgress] = useState(false)

  const navigate = useNavigate()

  // Fetch progress tracking data for a session
  const fetchProgressData = useCallback(async (sessionId) => {
    try {
      setLoadingProgress(true)
      const response = await sessionAPI.getProgressDetails(sessionId)
      if (response.success) {
        setProgressData(response.data)
      }
    } catch (error) {
      console.error('Error fetching progress data:', error)
      toast.error('Failed to load progress data')
    } finally {
      setLoadingProgress(false)
    }
  }, [])

  // Certificate download function
  const handleDownloadCertificate = async (session) => {
    try {
      if (session.status !== 'completed') {
        toast.error('Certificate is only available for completed sessions');
        return;
      }
      
      // Generate certificate token if not exists
      let certificateToken = session.certificate_token;
      if (!certificateToken) {
        // In a real implementation, you'd call an API to generate the certificate
        certificateToken = `cert_${session.id}_${Date.now()}`;
      }
      
      await certificateAPI.downloadCertificate(certificateToken);
      toast.success('Certificate downloaded successfully!');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    }
  };

  // Award virtual prizes function
  const handleAwardPrizes = async (session) => {
    try {
      if (session.status !== 'completed') {
        toast.error('Prizes can only be awarded for completed sessions');
        return;
      }
      
      await certificateAPI.awardPrizes(session.id);
      toast.success('Virtual prizes awarded successfully!');
    } catch (error) {
      console.error('Error awarding prizes:', error);
      toast.error('Failed to award virtual prizes');
    }
  };

  // Fetch sessions data
  const fetchSessions = async () => {
    try {
      setLoading(true)
      const response = await sessionAPI.getAll()
      setSessions(response.data || [])
      setError(null)
    } catch (err) {
      setError('Failed to fetch sessions')
      console.error('Error fetching sessions:', err)
    } finally {
      setLoading(false)
    }
  }

  // Auto-refresh every 30 seconds
  useEffect(() => {
    fetchSessions()
    
    if (autoRefresh) {
      const interval = setInterval(fetchSessions, 30000)
      return () => clearInterval(interval)
    }
  }, [autoRefresh])

  // Filter and sort sessions
  const filteredSessions = sessions
    .filter(session => {
      const matchesSearch = 
        session.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.access_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase())
      
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'refunded' ? session.is_refunded : session.status === statusFilter)
      const matchesHunt = huntFilter === 'all' || session.hunt_name === huntFilter
      
      return matchesSearch && matchesStatus && matchesHunt
    })
    .sort((a, b) => {
      let aValue = a[sortBy]
      let bValue = b[sortBy]
      
      if (sortBy === 'created_at' || sortBy === 'started_at' || sortBy === 'expires_at') {
        aValue = new Date(aValue || 0)
        bValue = new Date(bValue || 0)
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

  // Get status badge styling
  const getStatusBadge = (status, startedAt, isRefunded) => {
    let statusConfig;
    
    if (isRefunded) {
      statusConfig = { color: 'bg-orange-100 text-orange-800', icon: RotateCcw, text: 'Refunded' };
    } else if (status === 'active' && !startedAt) {
      // Active but not started
      statusConfig = { color: 'bg-blue-100 text-blue-800', icon: AlertCircle, text: 'Active (Not Started)' };
    } else if (status === 'active' && startedAt) {
      // Active and started
      statusConfig = { color: 'bg-green-100 text-green-800', icon: Play, text: 'Active' };
    } else if (status === 'completed') {
      statusConfig = { color: 'bg-blue-100 text-blue-800', icon: CheckCircle, text: 'Completed' };
    } else if (status === 'expired') {
      statusConfig = { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Expired' };
    } else {
      statusConfig = { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, text: status.charAt(0).toUpperCase() + status.slice(1) };
    }
    
    const Icon = statusConfig.icon
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {statusConfig.text}
      </span>
    )
  }

  // Get time remaining for active sessions
  const getTimeRemaining = (expiresAt, startedAt) => {
    // If session hasn't started yet, show "Not Started"
    if (!startedAt) return 'Not Started'
    
    // If no expiration date (shouldn't happen for started sessions, but handle it)
    if (!expiresAt) return 'N/A'
    
    const now = new Date()
    const expires = new Date(expiresAt)
    const diff = expires - now
    
    if (diff <= 0) return 'Expired'
    
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    
    return `${hours}h ${minutes}m`
  }

  // Handle session actions
  const handleExpireSession = async (sessionId) => {
    if (window.confirm('Are you sure you want to expire this session?')) {
      try {
        await sessionAPI.expire(sessionId)
        fetchSessions() // Refresh the list
      } catch (err) {
        console.error('Error expiring session:', err)
      }
    }
  }

  const handleDeleteSession = async (sessionId) => {
    try {
      setDeleting(true)
      await sessionAPI.deletePermanently(sessionId)
      
      // Close modal and refresh
      setShowDeleteModal(false)
      setSessionToDelete(null)
      fetchSessions()
      
      // Show success message (you could add a toast notification here)
    } catch (error) {
      console.error('Error deleting session:', error)
      setError('Failed to delete session')
    } finally {
      setDeleting(false)
    }
  }

  const openDeleteModal = (session) => {
    setSessionToDelete(session)
    setShowDeleteModal(true)
  }

  const openRefundModal = (session) => {
    setSessionToRefund(session)
    setRefundReason('')
    setShowRefundModal(true)
  }

  const handleRefundSession = async () => {
    try {
      setRefunding(true)
      await sessionAPI.refund(sessionToRefund.id, refundReason)
      
      // Close modal and refresh
      setShowRefundModal(false)
      setSessionToRefund(null)
      setRefundReason('')
      fetchSessions()
      
      // Show success message (you could add a toast notification here)
    } catch (error) {
      console.error('Error refunding session:', error)
      setError('Failed to refund session')
    } finally {
      setRefunding(false)
    }
  }

  const handleRefresh = () => {
    fetchSessions()
  }

  // Session details modal - memoized to prevent recreation on every render
  const SessionDetailsModal = React.memo(({ session, isOpen, onClose }) => {
    if (!isOpen || !session) return null

    // Manual load progress data - no automatic fetching to prevent loops

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold">Session Details</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <XCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Customer Name</label>
                  <p className="text-gray-900">{session.customer_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Customer Email</label>
                  <p className="text-gray-900">{session.customer_email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Hunt</label>
                  <p className="text-gray-900">{session.hunt_name || 'Unknown Hunt'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Access Code</label>
                  <p className="text-gray-900 font-mono">{session.access_code}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Booking Reference</label>
                  <p className="text-gray-900">{session.booking_reference}</p>
                </div>
              </div>
            
            <div className="space-y-3">
                             <div>
                 <label className="text-sm font-medium text-gray-500">Status</label>
                 <div className="mt-1">{getStatusBadge(session.status, session.started_at)}</div>
               </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Created</label>
                <p className="text-gray-900">
                  {session.created_at ? format(new Date(session.created_at), 'PPP p') : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Started</label>
                <p className="text-gray-900">
                  {session.started_at ? format(new Date(session.started_at), 'PPP p') : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Expires</label>
                <p className="text-gray-900">
                  {session.expires_at ? format(new Date(session.expires_at), 'PPP p') : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Enhanced Progress Tracking Section */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                Progress Tracking
              </h4>
              <button
                onClick={() => fetchProgressData(session.id)}
                disabled={loadingProgress}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {loadingProgress ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Load Progress
                  </>
                )}
              </button>
            </div>
            
            {loadingProgress ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Loading progress data...</span>
              </div>
            ) : progressData ? (
              <div className="space-y-4">
                {/* Progress Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-sm text-blue-600 font-medium">Locations</div>
                    <div className="text-xl font-bold text-blue-900">
                      {progressData.locations || 0}
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-sm text-green-600 font-medium">Correct</div>
                    <div className="text-xl font-bold text-green-900">
                      {progressData.correct || 0}
                    </div>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <div className="text-sm text-orange-600 font-medium">Hints Used</div>
                    <div className="text-xl font-bold text-orange-900">
                      {progressData.hintsUsed || 0}
                    </div>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <div className="text-sm text-purple-600 font-medium">Avg Time</div>
                    <div className="text-xl font-bold text-purple-900">
                      {progressData.avgTime || 0}s
                    </div>
                  </div>
                </div>

                {/* Detailed Progress */}
                {progressData.progress && progressData.progress.length > 0 && (
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">Location Progress</h5>
                    <div className="space-y-2">
                      {progressData.progress.map((progress, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-gray-900">
                                Location {progress.location_index}: {progress.location_name}
                              </div>
                              <div className="text-sm text-gray-600">
                                Answer: {progress.answer_provided || 'No answer'}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`text-sm font-medium ${
                                progress.is_correct ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {progress.is_correct ? '✓ Correct' : '✗ Incorrect'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {progress.time_spent_seconds}s
                                {progress.hints_requested > 0 && ` • ${progress.hints_requested} hints`}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Offline Stats */}
                {progressData.session?.total_offline_duration_seconds > 0 && (
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">Offline Activity</h5>
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <div className="text-sm text-yellow-800">
                        <strong>Total offline time:</strong> {Math.round(progressData.session.total_offline_duration_seconds / 60)} minutes
                      </div>
                      <div className="text-sm text-yellow-800">
                        <strong>Sync attempts:</strong> {progressData.session.sync_attempts || 0}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                No progress tracking data available for this session
              </div>
            )}
          </div>
          
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
              <div>
                <label className="text-sm font-medium text-gray-500">Time Remaining</label>
                <p className="text-lg font-semibold text-gray-900">
                  {getTimeRemaining(session.expires_at, session.started_at)}
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <button
                  onClick={() => handleExpireSession(session.id)}
                  className="px-4 py-3 sm:py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center justify-center"
                >
                  <Square className="w-4 h-4 mr-2" />
                  Expire Session
                </button>
                
                <button
                  onClick={() => {
                    onClose()
                    openDeleteModal(session)
                  }}
                  className="px-4 py-3 sm:py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center justify-center"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  })

  return (
    <div className="p-4 lg:p-6">
      {/* Header with clean styling */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Live Sessions</h1>
              <p className="text-sm lg:text-base text-gray-600">Monitor and manage active hunt sessions</p>
            </div>
          </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={() => {
              // Navigate to Settings page with manual codes tab
              navigate('/settings/manual-codes')
            }}
            className="btn-primary flex items-center justify-center"
          >
            <Key className="w-4 h-4 mr-2" />
            Create Manual Code
          </button>
          
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="btn-secondary flex items-center justify-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          <label className="flex items-center justify-center space-x-2 p-2 bg-gray-50 rounded-md">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-600">Auto-refresh</span>
          </label>
        </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Play className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Active</p>
              <p className="text-xl font-bold text-gray-900">
                {sessions.filter(s => s.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Completed</p>
              <p className="text-xl font-bold text-gray-900">
                {sessions.filter(s => s.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Expired</p>
              <p className="text-xl font-bold text-gray-900">
                {sessions.filter(s => s.status === 'expired').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <RotateCcw className="w-5 h-5 text-orange-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Refunded</p>
              <p className="text-xl font-bold text-gray-900">
                {sessions.filter(s => s.is_refunded).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Users className="w-5 h-5 text-gray-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-600">Total</p>
              <p className="text-xl font-bold text-gray-900">{sessions.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="expired">Expired</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <select
                value={huntFilter}
                onChange={(e) => setHuntFilter(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
              >
                <option value="all">All Hunts</option>
                {Array.from(new Set(sessions.map(s => s.hunt_name))).map(huntName => (
                  <option key={huntName} value={huntName}>{huntName}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 whitespace-nowrap">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
              >
                <option value="created_at">Created</option>
                <option value="started_at">Started</option>
                <option value="expires_at">Expires</option>
                <option value="customer_name">Name</option>
                <option value="status">Status</option>
              </select>
              
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="p-3 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center justify-center"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="card">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
            <p className="mt-2 text-sm text-gray-500">Loading sessions...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertCircle className="mx-auto h-8 w-8 text-red-400" />
            <p className="mt-2 text-sm text-red-500">{error}</p>
            <button
              onClick={fetchSessions}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Try Again
            </button>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'No hunt sessions have been created yet'
              }
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Access Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hunt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time Remaining
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progress
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSessions.map((session) => (
                    <tr key={session.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {session.customer_name}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <Mail className="w-3 h-3 mr-1" />
                            {session.customer_email}
                          </div>
                          <div className="text-xs text-gray-400 flex items-center">
                            <Hash className="w-3 h-3 mr-1" />
                            {session.booking_reference}
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                          {session.access_code}
                        </span>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <MapPin className="w-3 h-3 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {session.hunt_name || 'Unknown Hunt'}
                          </span>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(session.status, session.started_at, session.is_refunded)}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {session.status === 'active' ? (
                          <div className="flex items-center">
                            {session.started_at ? (
                              <>
                                <Clock className="w-4 h-4 text-orange-500 mr-2" />
                                <span className="text-sm font-medium text-orange-600">
                                  {getTimeRemaining(session.expires_at, session.started_at)}
                                </span>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-4 h-4 text-blue-500 mr-2" />
                                <span className="text-sm font-medium text-blue-600">
                                  Not Started
                                </span>
                              </>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">
                            {session.status === 'completed' ? 'Completed' : 'Expired'}
                          </span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1">
                          {session.status === 'active' || session.status === 'completed' ? (
                            <>
                              <div className="flex items-center">
                                <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
                                <span className="text-xs text-blue-600 font-medium">Enhanced</span>
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedSession(session)
                                  setShowDetails(true)
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs underline"
                              >
                                View Details
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">No progress</span>
                          )}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {session.created_at ? formatDistanceToNow(new Date(session.created_at), { addSuffix: true }) : 'N/A'}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedSession(session)
                              setShowDetails(true)
                            }}
                            className="text-primary-600 hover:text-primary-900 p-1"
                            title="View Details"
                          >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {session.status === 'completed' && (
                          <>
                            <button
                              onClick={() => handleDownloadCertificate(session)}
                              className="text-green-600 hover:text-green-900 p-1"
                              title="Download Certificate"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAwardPrizes(session)}
                              className="text-purple-600 hover:text-purple-900 p-1"
                              title="Award Virtual Prizes"
                            >
                              <Award className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        
                        {session.status === 'active' && !session.is_refunded && (
                          <button
                            onClick={() => handleExpireSession(session.id)}
                            className="text-orange-600 hover:text-orange-900 p-1"
                            title="Expire Session"
                          >
                            <Square className="w-4 h-4" />
                          </button>
                        )}
                        
                        {!session.is_refunded && (
                          <button
                            onClick={() => openRefundModal(session)}
                            className="text-orange-600 hover:text-orange-900 p-1"
                            title="Refund Session"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => openDeleteModal(session)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Delete Session & User Data"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {filteredSessions.map((session) => (
              <div key={session.id} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                {/* Customer Info */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">
                      {session.customer_name}
                    </h3>
                    <div className="text-sm text-gray-500 flex items-center mt-1">
                      <Mail className="w-3 h-3 mr-1" />
                      {session.customer_email}
                    </div>
                    <div className="text-xs text-gray-400 flex items-center mt-1">
                      <Hash className="w-3 h-3 mr-1" />
                      {session.booking_reference}
                    </div>
                  </div>
                  <div className="ml-4">
                    {getStatusBadge(session.status, session.started_at, session.is_refunded)}
                  </div>
                </div>

                {/* Access Code */}
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-gray-500">Code:</span>
                  <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                    {session.access_code}
                  </span>
                </div>

                {/* Hunt */}
                <div className="flex items-center space-x-2">
                  <MapPin className="w-3 h-3 text-gray-400" />
                  <span className="text-sm text-gray-900">
                    {session.hunt_name || 'Unknown Hunt'}
                  </span>
                </div>

                {/* Time Remaining */}
                <div className="flex items-center space-x-2">
                  {session.status === 'active' ? (
                    session.started_at ? (
                      <>
                        <Clock className="w-4 h-4 text-orange-500" />
                        <span className="text-sm font-medium text-orange-600">
                          {getTimeRemaining(session.expires_at, session.started_at)}
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium text-blue-600">
                          Not Started
                        </span>
                      </>
                    )
                  ) : (
                    <span className="text-sm text-gray-500">
                      {session.status === 'completed' ? 'Completed' : 'Expired'}
                    </span>
                  )}
                </div>

                {/* Created Date */}
                <div className="flex items-center space-x-2">
                  <Calendar className="w-3 h-3 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {session.created_at ? formatDistanceToNow(new Date(session.created_at), { addSuffix: true }) : 'N/A'}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setSelectedSession(session)
                      setShowDetails(true)
                    }}
                    className="text-primary-600 hover:text-primary-900 p-2"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  
                  {session.status === 'completed' && (
                    <>
                      <button
                        onClick={() => handleDownloadCertificate(session)}
                        className="text-green-600 hover:text-green-900 p-2"
                        title="Download Certificate"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleAwardPrizes(session)}
                        className="text-purple-600 hover:text-purple-900 p-2"
                        title="Award Virtual Prizes"
                      >
                        <Award className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  
                  {session.status === 'active' && !session.is_refunded && (
                    <button
                      onClick={() => handleExpireSession(session.id)}
                      className="text-orange-600 hover:text-orange-900 p-2"
                      title="Expire Session"
                    >
                      <Square className="w-4 h-4" />
                    </button>
                  )}
                  
                  {!session.is_refunded && (
                    <button
                      onClick={() => openRefundModal(session)}
                      className="text-orange-600 hover:text-orange-900 p-2"
                      title="Refund Session"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                  
                  <button
                    onClick={() => openDeleteModal(session)}
                    className="text-red-600 hover:text-red-900 p-2"
                    title="Delete Session & User Data"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>

    {/* Session Details Modal */}
      <SessionDetailsModal
        session={selectedSession}
        isOpen={showDetails}
        onClose={() => {
          setShowDetails(false)
          setSelectedSession(null)
          setProgressData(null) // Clear progress data when modal closes
        }}
      />

      {/* Refund Confirmation Modal */}
      {showRefundModal && sessionToRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-orange-100 rounded-lg mr-3">
                <RotateCcw className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Refund Session</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-3">
                Are you sure you want to <strong>refund</strong> this session?
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-sm text-orange-800">
                  <strong>Note:</strong> This will:
                </p>
                <ul className="text-sm text-orange-700 mt-2 space-y-1">
                  <li>• Mark the session as refunded</li>
                  <li>• Exclude it from revenue calculations</li>
                  <li>• Keep the session data for reference</li>
                  <li>• Allow you to track refund reasons</li>
                </ul>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Refund Reason (Optional)
                </label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="e.g., Customer request, Technical issue, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  rows="3"
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => {
                  setShowRefundModal(false)
                  setSessionToRefund(null)
                  setRefundReason('')
                }}
                className="px-4 py-3 sm:py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={refunding}
              >
                Cancel
              </button>
              <button
                onClick={handleRefundSession}
                className="px-4 py-3 sm:py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={refunding}
              >
                {refunding ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Refunding...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Refund Session
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && sessionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-red-100 rounded-lg mr-3">
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Session</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-3">
                Are you sure you want to <strong>permanently delete</strong> this session?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> This will permanently remove:
                </p>
                <ul className="text-sm text-red-700 mt-2 space-y-1">
                  <li>• Customer name and email</li>
                  <li>• Access code and booking reference</li>
                  <li>• All session data and progress</li>
                  <li>• This action cannot be undone</li>
                </ul>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setSessionToDelete(null)
                }}
                className="px-4 py-3 sm:py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSession(sessionToDelete.id)}
                className="px-4 py-3 sm:py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Permanently
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Sessions
